import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Plus, Music, Image as ImageIcon, Upload, Star, Edit2, Trash2, Save, ChevronUp, ChevronDown, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DateTimePicker } from '../components/ui/datetime-picker';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('eps');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [eps, setEps] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedEP, setSelectedEP] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  
  // Form states
  const [epForm, setEpForm] = useState({ 
    title: '', 
    description: '', 
    release_date: '', 
    cover_art: '',
    published: true,
    scheduled_publish_date: '',
    timezone: 'UTC'
  });
  const [trackForm, setTrackForm] = useState({ 
    title: '', 
    duration: '', 
    youtube_url: '', 
    audio_url: '', 
    ep_id: '', 
    order: 0,
    published: true,
    scheduled_publish_date: '',
    timezone: 'Asia/Seoul',
    is_featured: false
  });
  const [settingsForm, setSettingsForm] = useState({ bio: '', email: '', youtube: '', instagram: '', profile_image_url: '' });
  
  // Edit states
  const [editingEP, setEditingEP] = useState(null);
  const [editingTrack, setEditingTrack] = useState(null);
  const [editEPForm, setEditEPForm] = useState({ 
    title: '', 
    description: '', 
    release_date: '', 
    cover_art: '',
    published: true,
    scheduled_publish_date: '',
    timezone: 'UTC'
  });
  const [editTrackForm, setEditTrackForm] = useState({ title: '', duration: '', youtube_url: '', audio_url: '' });
  
  // Upload states
  const [uploading, setUploading] = useState(false);

  // True when an EP/track is configured as scheduled AND the scheduled time
  // is still in the future. Once the time has passed, the item is considered
  // live and the "Scheduled" badge should disappear.
  const isStillScheduled = (item) => {
    if (item?.published) return false;
    if (!item?.scheduled_publish_date) return true; // unpublished, no date => still hidden
    return new Date(item.scheduled_publish_date) > new Date();
  };

  const fetchData = useCallback(async () => {
    try {
      const [epsResponse, settingsResponse, analyticsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/eps?show_all=true`), // Show all tracks in admin
        axios.get(`${BACKEND_URL}/api/settings`),
        axios.get(`${BACKEND_URL}/api/analytics/stats`, { withCredentials: true })
      ]);
      
      setEps(epsResponse.data);
      setSettings(settingsResponse.data);
      setAnalytics(analyticsResponse.data);
      setSettingsForm({
        bio: settingsResponse.data.bio,
        email: settingsResponse.data.email,
        youtube: settingsResponse.data.social_media?.youtube || '',
        instagram: settingsResponse.data.social_media?.instagram || '',
        profile_image_url: settingsResponse.data.profile_image_url || ''
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  // EP Management
  const createEP = async () => {
    if (!epForm.title || !epForm.release_date) {
      toast.error('Title and release date are required');
      return;
    }

    try {
      const epData = {
        ...epForm,
        scheduled_publish_date: !epForm.published && epForm.scheduled_publish_date 
          ? new Date(epForm.scheduled_publish_date).toISOString() 
          : null
      };
      
      await axios.post(`${BACKEND_URL}/api/eps`, epData, { withCredentials: true });
      toast.success(epForm.published ? 'EP created successfully' : 'EP scheduled successfully');
      setEpForm({ 
        title: '', 
        description: '', 
        release_date: '', 
        cover_art: '',
        published: true,
        scheduled_publish_date: '',
        timezone: 'UTC'
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create EP');
    }
  };

  const deleteEP = async (epId) => {
    if (!window.confirm('Delete this EP and all its tracks?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/eps/${epId}`, { withCredentials: true });
      toast.success('EP deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete EP');
    }
  };

  // Track Management
  const createTrack = async () => {
    if (!trackForm.title || !trackForm.ep_id) {
      toast.error('Title and EP are required');
      return;
    }

    try {
      // Get the current highest order number for this EP
      const ep = eps.find(e => e._id === trackForm.ep_id);
      const maxOrder = ep?.tracks?.length > 0 
        ? Math.max(...ep.tracks.map(t => t.order || 0))
        : -1;
      
      await axios.post(`${BACKEND_URL}/api/tracks`, {
        ...trackForm,
        order: maxOrder + 1,
        scheduled_publish_date: !trackForm.published && trackForm.scheduled_publish_date 
          ? new Date(trackForm.scheduled_publish_date).toISOString() 
          : null
      }, { withCredentials: true });
      
      toast.success(trackForm.published ? 'Track published successfully' : 'Track scheduled successfully');
      setTrackForm({ 
        title: '', 
        duration: '', 
        youtube_url: '', 
        audio_url: '', 
        ep_id: '', 
        order: 0,
        published: true,
        scheduled_publish_date: '',
        timezone: 'Asia/Seoul',
        is_featured: false
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create track');
    }
  };

  const deleteTrack = async (trackId) => {
    if (!window.confirm('Delete this track?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/tracks/${trackId}`, { withCredentials: true });
      toast.success('Track deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete track');
    }
  };

  const setFeaturedTrack = async (trackId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/tracks/${trackId}/feature`, {}, { withCredentials: true });
      toast.success('Featured track updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to set featured track');
    }
  };

  const moveTrack = async (trackId, direction, currentOrder, tracks) => {
    // Find the track to swap with
    const targetOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;
    const swapTrack = tracks.find(t => t.order === targetOrder);
    
    if (!swapTrack) {
      console.log('No track to swap with at order:', targetOrder);
      return; // Can't move if at boundary
    }

    try {
      // Update both tracks' order (swap positions)
      await Promise.all([
        axios.put(`${BACKEND_URL}/api/tracks/${trackId}`, { order: targetOrder }, { withCredentials: true }),
        axios.put(`${BACKEND_URL}/api/tracks/${swapTrack._id}`, { order: currentOrder }, { withCredentials: true })
      ]);
      
      toast.success('Track moved successfully');
      fetchData();
    } catch (error) {
      console.error('Move error:', error);
      toast.error('Failed to reorder track');
    }
  };

  // Edit EP
  const startEditEP = (ep) => {
    setEditingEP(ep._id);
    setEditEPForm({
      title: ep.title,
      description: ep.description,
      release_date: ep.release_date,
      cover_art: ep.cover_art || '',
      published: ep.published !== undefined ? ep.published : true,
      scheduled_publish_date: ep.scheduled_publish_date ? new Date(ep.scheduled_publish_date).toISOString().slice(0, 16) : '',
      timezone: ep.timezone || 'UTC'
    });
  };

  const updateEP = async (epId) => {
    try {
      const epData = {
        ...editEPForm,
        scheduled_publish_date: !editEPForm.published && editEPForm.scheduled_publish_date 
          ? new Date(editEPForm.scheduled_publish_date).toISOString() 
          : null
      };
      
      await axios.put(`${BACKEND_URL}/api/eps/${epId}`, epData, { withCredentials: true });
      toast.success('EP updated successfully');
      setEditingEP(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update EP');
    }
  };

  const cancelEditEP = () => {
    setEditingEP(null);
    setEditEPForm({ 
      title: '', 
      description: '', 
      release_date: '', 
      cover_art: '',
      published: true,
      scheduled_publish_date: '',
      timezone: 'UTC'
    });
  };

  // Edit Track
  const startEditTrack = (track) => {
    setEditingTrack(track._id);
    setEditTrackForm({
      title: track.title,
      duration: track.duration,
      youtube_url: track.youtube_url || '',
      audio_url: track.audio_url || ''
    });
  };

  const updateTrack = async (trackId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/tracks/${trackId}`, editTrackForm, { withCredentials: true });
      toast.success('Track updated successfully');
      setEditingTrack(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update track');
    }
  };

  const cancelEditTrack = () => {
    setEditingTrack(null);
    setEditTrackForm({ title: '', duration: '', youtube_url: '', audio_url: '' });
  };

  // File Upload
  const uploadFile = async (file, type) => {
    if (!file) return null;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/upload/${type}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        }
      );
      toast.success(`${type} uploaded successfully`);
      return response.data.url;
    } catch (error) {
      toast.error(`Failed to upload ${type}`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const url = await uploadFile(file, 'image');
    if (url) {
      setEpForm({ ...epForm, cover_art: url });
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate MP3 format
    if (!file.name.toLowerCase().endsWith('.mp3')) {
      toast.error('Please upload MP3 files only');
      e.target.value = ''; // Reset file input
      return;
    }

    const url = await uploadFile(file, 'audio');
    if (url) {
      setTrackForm({ ...trackForm, audio_url: url });
    }
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const url = await uploadFile(file, 'image');
    if (url) {
      setSettingsForm({ ...settingsForm, profile_image_url: url });
    }
  };

  const handleEditEPCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const url = await uploadFile(file, 'image');
    if (url) {
      setEditEPForm({ ...editEPForm, cover_art: url });
    }
  };

  const handleEditTrackAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate MP3 format
    if (!file.name.toLowerCase().endsWith('.mp3')) {
      toast.error('Please upload MP3 files only');
      e.target.value = ''; // Reset file input
      return;
    }

    const url = await uploadFile(file, 'audio');
    if (url) {
      setEditTrackForm({ ...editTrackForm, audio_url: url });
    }
  };

  // Settings Management
  const updateSettings = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/settings`,
        {
          bio: settingsForm.bio,
          email: settingsForm.email,
          social_media: {
            youtube: settingsForm.youtube,
            instagram: settingsForm.instagram
          },
          profile_image_url: settingsForm.profile_image_url
        },
        { withCredentials: true }
      );
      toast.success('Settings updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-purple-400">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-lg border-b border-purple-500/20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src={settings?.logo}
              alt="PN0VΛ"
              className="h-10 w-auto"
            />
            <span className="text-purple-300">Admin Dashboard</span>
          </div>
          
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-purple-300 hover:text-white hover:bg-purple-500/20"
            >
              View Site
            </Button>
            <Button 
              variant="ghost"
              onClick={handleLogout}
              className="text-purple-300 hover:text-white hover:bg-purple-500/20"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-purple-500/20">
          <button
            onClick={() => setActiveTab('eps')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'eps' 
                ? 'text-purple-400 border-b-2 border-purple-400' 
                : 'text-purple-300 hover:text-white'
            }`}
          >
            <Music className="inline mr-2 h-4 w-4" />
            EPs & Tracks
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'settings' 
                ? 'text-purple-400 border-b-2 border-purple-400' 
                : 'text-purple-300 hover:text-white'
            }`}
          >
            Bio & Settings
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'analytics' 
                ? 'text-purple-400 border-b-2 border-purple-400' 
                : 'text-purple-300 hover:text-white'
            }`}
          >
            📊 Analytics
          </button>
        </div>

        {/* EPs & Tracks Tab */}
        {activeTab === 'eps' && (
          <div className="space-y-8">
            {/* Existing EPs */}
            <div>
              <h2 className="text-2xl font-bold mb-4">Your EPs</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {eps.map(ep => (
                  <Card key={ep._id} className="bg-gradient-to-br from-purple-900/20 to-black border-purple-500/30 p-6">
                    {editingEP === ep._id ? (
                      /* Edit Mode */
                      <div className="space-y-4">
                        {/* Current Cover Preview */}
                        {editEPForm.cover_art && (
                          <div className="flex items-center gap-4">
                            <img 
                              src={`${BACKEND_URL}${editEPForm.cover_art}`}
                              alt="Current cover"
                              onError={(e) => {
                                e.target.src = 'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=800&auto=format&fit=crop';
                              }}
                              className="w-24 h-24 rounded-lg object-cover"
                            />
                            <div className="text-purple-300 text-sm">Current cover image</div>
                          </div>
                        )}
                        
                        {/* Cover Image Upload */}
                        <div>
                          <Label className="text-purple-300">Cover Image</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleEditEPCoverUpload}
                              className="hidden"
                              id={`edit-cover-upload-${ep._id}`}
                            />
                            <label htmlFor={`edit-cover-upload-${ep._id}`}>
                              <Button
                                type="button"
                                variant="outline"
                                className="border-purple-500/30 text-purple-300 cursor-pointer"
                                asChild
                              >
                                <span>
                                  <Upload className="mr-2 h-4 w-4" />
                                  {uploading ? 'Uploading...' : 'Change Cover Image'}
                                </span>
                              </Button>
                            </label>
                          </div>
                        </div>

                        <div>
                          <Label className="text-purple-300">Title</Label>
                          <Input
                            value={editEPForm.title}
                            onChange={(e) => setEditEPForm({ ...editEPForm, title: e.target.value })}
                            className="bg-black/50 border-purple-500/30 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-purple-300">Description</Label>
                          <Textarea
                            value={editEPForm.description}
                            onChange={(e) => setEditEPForm({ ...editEPForm, description: e.target.value })}
                            className="bg-black/50 border-purple-500/30 text-white"
                            rows={2}
                          />
                        </div>
                        <div>
                          <Label className="text-purple-300">Release Year</Label>
                          <Input
                            value={editEPForm.release_date}
                            onChange={(e) => setEditEPForm({ ...editEPForm, release_date: e.target.value })}
                            className="bg-black/50 border-purple-500/30 text-white"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-purple-300">Publishing</Label>
                          <div className="flex items-center gap-2 mb-2">
                            <input
                              type="checkbox"
                              id="edit-ep-published"
                              checked={editEPForm.published}
                              onChange={(e) => setEditEPForm({ ...editEPForm, published: e.target.checked })}
                              className="w-4 h-4 rounded border-purple-500/30"
                            />
                            <label htmlFor="edit-ep-published" className="text-white text-sm cursor-pointer">
                              Publish immediately
                            </label>
                          </div>
                          
                          {!editEPForm.published && (
                            <div className="space-y-2 mt-2 p-3 bg-purple-900/20 rounded-lg">
                              <Label className="text-purple-300 text-xs">Schedule Publish Date</Label>
                              <DateTimePicker
                                value={editEPForm.scheduled_publish_date}
                                onChange={(v) => setEditEPForm({ ...editEPForm, scheduled_publish_date: v })}
                              />
                              <Label className="text-purple-300 text-xs">Timezone</Label>
                              <select
                                value={editEPForm.timezone}
                                onChange={(e) => setEditEPForm({ ...editEPForm, timezone: e.target.value })}
                                className="w-full bg-black/50 border border-purple-500/30 text-white rounded-lg p-2"
                              >
                                <option value="UTC">UTC</option>
                                <option value="Asia/Seoul">Asia/Seoul (KST)</option>
                                <option value="America/New_York">America/New_York (EST)</option>
                                <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                                <option value="Europe/London">Europe/London (GMT)</option>
                              </select>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            onClick={() => updateEP(ep._id)}
                            className="bg-purple-600 hover:bg-purple-500"
                            disabled={uploading}
                          >
                            <Save className="mr-2 h-4 w-4" />
                            Save Changes
                          </Button>
                          <Button
                            variant="outline"
                            onClick={cancelEditEP}
                            className="border-purple-500/30 text-purple-300"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <>
                        <div className="flex gap-4 mb-4">
                          {ep.cover_art && (
                            <img 
                              src={`${BACKEND_URL}${ep.cover_art}`}
                              alt={ep.title}
                              onError={(e) => {
                                e.target.src = 'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=800&auto=format&fit=crop';
                              }}
                              className="w-24 h-24 rounded-lg object-cover"
                            />
                          )}
                          {!ep.cover_art && (
                            <div className="w-24 h-24 rounded-lg bg-purple-900/20 flex items-center justify-center">
                              <span className="text-purple-400 text-xs">No image</span>
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-xl font-bold text-white">{ep.title}</h3>
                              {isStillScheduled(ep) && (
                                <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded">
                                  Scheduled
                                </span>
                              )}
                            </div>
                            <p className="text-purple-300 text-sm mb-2">{ep.description}</p>
                            <p className="text-purple-400 text-xs">{ep.release_date}</p>
                          </div>
                          <div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEditEP(ep)}
                              className="text-purple-300 hover:text-white"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                    
                    {/* Tracks */}
                    <div className="space-y-2 mb-4">
                      <p className="text-purple-300 text-sm font-medium">Tracks ({ep.tracks?.length || 0})</p>
                      {ep.tracks?.sort((a, b) => a.order - b.order).map((track, index) => (
                        <div key={track._id}>
                          {editingTrack === track._id ? (
                            /* Edit Track Mode */
                            <div className="bg-purple-900/20 p-3 rounded space-y-2">
                              <Input
                                placeholder="Track title"
                                value={editTrackForm.title}
                                onChange={(e) => setEditTrackForm({ ...editTrackForm, title: e.target.value })}
                                className="bg-black/50 border-purple-500/30 text-white text-sm"
                              />
                              <div className="flex gap-2">
                                <Input
                                  placeholder="Duration (3:45)"
                                  value={editTrackForm.duration}
                                  onChange={(e) => setEditTrackForm({ ...editTrackForm, duration: e.target.value })}
                                  className="bg-black/50 border-purple-500/30 text-white text-sm flex-1"
                                />
                                <Input
                                  placeholder="YouTube URL"
                                  value={editTrackForm.youtube_url}
                                  onChange={(e) => setEditTrackForm({ ...editTrackForm, youtube_url: e.target.value })}
                                  className="bg-black/50 border-purple-500/30 text-white text-sm flex-1"
                                />
                              </div>
                              
                              {/* Audio Upload */}
                              <div>
                                <Label className="text-purple-300 text-xs">Audio File</Label>
                                <div className="flex items-center gap-2">
                                  {editTrackForm.audio_url && (
                                    <span className="text-green-400 text-xs">✓ Audio file set</span>
                                  )}
                                  {!editTrackForm.audio_url && (
                                    <span className="text-orange-400 text-xs">⚠ No audio file</span>
                                  )}
                                  <input
                                    type="file"
                                    accept=".mp3,audio/mpeg"
                                    onChange={handleEditTrackAudioUpload}
                                    className="hidden"
                                    id={`edit-audio-upload-${track._id}`}
                                  />
                                  <label htmlFor={`edit-audio-upload-${track._id}`}>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="border-purple-500/30 text-purple-300 cursor-pointer text-xs h-7"
                                      asChild
                                    >
                                      <span>
                                        <Upload className="mr-1 h-3 w-3" />
                                        {uploading ? 'Uploading...' : editTrackForm.audio_url ? 'Change MP3' : 'Upload MP3'}
                                      </span>
                                    </Button>
                                  </label>
                                </div>
                              </div>

                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => updateTrack(track._id)}
                                  className="bg-purple-600 hover:bg-purple-500"
                                  disabled={uploading}
                                >
                                  <Save className="mr-1 h-3 w-3" />
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={cancelEditTrack}
                                  className="border-purple-500/30 text-purple-300"
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            /* View Track Mode */
                            <div className="flex justify-between items-center bg-black/30 p-2 rounded">
                              <div className="flex items-center gap-2 flex-1">
                                {/* Move Up/Down Buttons */}
                                <div className="flex flex-col gap-0">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => moveTrack(track._id, 'up', track.order, ep.tracks)}
                                    disabled={index === 0}
                                    className="h-4 w-6 p-0 text-purple-400 hover:text-white disabled:opacity-30"
                                    title="Move up"
                                  >
                                    <ChevronUp className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => moveTrack(track._id, 'down', track.order, ep.tracks)}
                                    disabled={index === ep.tracks.length - 1}
                                    className="h-4 w-6 p-0 text-purple-400 hover:text-white disabled:opacity-30"
                                    title="Move down"
                                  >
                                    <ChevronDown className="h-3 w-3" />
                                  </Button>
                                </div>
                                
                                <span className="text-white text-sm flex-1">{track.title}</span>
                                {track.is_featured && !isStillScheduled(track) && (
                                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" title="Featured (Live)" />
                                )}
                                {track.is_featured && isStillScheduled(track) && (
                                  <Star className="h-4 w-4 text-orange-400 fill-orange-400" title="Featured (Scheduled)" />
                                )}
                                {isStillScheduled(track) && (
                                  <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded">
                                    Scheduled
                                  </span>
                                )}
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => startEditTrack(track)}
                                  className="text-purple-300 hover:text-white h-7 w-7 p-0"
                                  title="Edit track"
                                >
                                  <Edit2 className="h-3 w-3" />
                                </Button>
                                {!track.is_featured && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setFeaturedTrack(track._id)}
                                    className="text-purple-300 hover:text-white h-7 w-7 p-0"
                                    title={track.published ? "Set as Featured Track" : "Set as Featured (Will show when published)"}
                                  >
                                    <Star className="h-4 w-4" />
                                  </Button>
                                )}
                                {track.is_featured && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setFeaturedTrack(track._id)}
                                    className="text-yellow-400 hover:text-yellow-300 h-7 w-7 p-0"
                                    title="Featured track"
                                  >
                                    <Star className="h-4 w-4 fill-current" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteTrack(track._id)}
                                  className="text-red-400 hover:text-red-300 h-7 w-7 p-0"
                                  title="Delete track"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => deleteEP(ep._id)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete EP
                    </Button>
                    </>
                    )}
                  </Card>
                ))}
              </div>
            </div>

            {/* Create New EP */}
            <Card className="bg-gradient-to-br from-purple-900/20 to-black border-purple-500/30 p-6">
              <h3 className="text-xl font-bold mb-6">Create New EP</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-purple-300">EP Title *</Label>
                    <Input 
                      value={epForm.title}
                      onChange={(e) => setEpForm({ ...epForm, title: e.target.value })}
                      placeholder="Enter EP title"
                      className="bg-black/50 border-purple-500/30 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-purple-300">Description</Label>
                    <Textarea 
                      value={epForm.description}
                      onChange={(e) => setEpForm({ ...epForm, description: e.target.value })}
                      placeholder="Enter description"
                      className="bg-black/50 border-purple-500/30 text-white"
                      rows={3}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-purple-300">Release Year *</Label>
                    <Input 
                      value={epForm.release_date}
                      onChange={(e) => setEpForm({ ...epForm, release_date: e.target.value })}
                      placeholder="2024"
                      className="bg-black/50 border-purple-500/30 text-white"
                    />
                  </div>

                  <div>
                    <Label className="text-purple-300">Publishing</Label>
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        id="ep-published"
                        checked={epForm.published}
                        onChange={(e) => setEpForm({ ...epForm, published: e.target.checked })}
                        className="w-4 h-4 rounded border-purple-500/30"
                      />
                      <label htmlFor="ep-published" className="text-white text-sm cursor-pointer">
                        Publish immediately
                      </label>
                    </div>
                    
                    {!epForm.published && (
                      <div className="space-y-2 mt-2 p-3 bg-purple-900/20 rounded-lg">
                        <Label className="text-purple-300 text-xs">Schedule Publish Date</Label>
                        <DateTimePicker
                          value={epForm.scheduled_publish_date}
                          onChange={(v) => setEpForm({ ...epForm, scheduled_publish_date: v })}
                        />
                        <Label className="text-purple-300 text-xs">Timezone</Label>
                        <select
                          value={epForm.timezone}
                          onChange={(e) => setEpForm({ ...epForm, timezone: e.target.value })}
                          className="w-full bg-black/50 border border-purple-500/30 text-white rounded-lg p-2"
                        >
                          <option value="UTC">UTC</option>
                          <option value="Asia/Seoul">Asia/Seoul (KST)</option>
                          <option value="America/New_York">America/New_York (EST)</option>
                          <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                          <option value="Europe/London">Europe/London (GMT)</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-purple-300">Cover Art</Label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleCoverUpload}
                      className="hidden"
                      id="cover-upload"
                      disabled={uploading}
                    />
                    <label htmlFor="cover-upload">
                      <div className="border-2 border-dashed border-purple-500/30 rounded-lg p-8 text-center hover:border-purple-500/50 transition-colors cursor-pointer">
                        <ImageIcon className="mx-auto h-12 w-12 text-purple-400 mb-2" />
                        <p className="text-purple-300 text-sm">
                          {uploading ? 'Uploading...' : epForm.cover_art ? 'Image uploaded ✓' : 'Click to upload'}
                        </p>
                      </div>
                    </label>
                  </div>

                  <Button 
                    onClick={createEP}
                    className="w-full bg-purple-600 hover:bg-purple-500"
                    disabled={uploading}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create EP
                  </Button>
                </div>

                {/* Add Track */}
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-purple-300">Add Track to EP</h4>
                  
                  <div>
                    <Label className="text-purple-300">Select EP *</Label>
                    <select
                      value={trackForm.ep_id}
                      onChange={(e) => setTrackForm({ ...trackForm, ep_id: e.target.value })}
                      className="w-full bg-black/50 border border-purple-500/30 text-white rounded-lg p-2"
                    >
                      <option value="">Select an EP</option>
                      {eps.map(ep => (
                        <option key={ep._id} value={ep._id}>{ep.title}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label className="text-purple-300">Track Title *</Label>
                    <Input 
                      value={trackForm.title}
                      onChange={(e) => setTrackForm({ ...trackForm, title: e.target.value })}
                      placeholder="Enter track title"
                      className="bg-black/50 border-purple-500/30 text-white"
                    />
                  </div>

                  <div>
                    <Label className="text-purple-300">Duration (e.g., 3:45)</Label>
                    <Input 
                      value={trackForm.duration}
                      onChange={(e) => setTrackForm({ ...trackForm, duration: e.target.value })}
                      placeholder="3:45"
                      className="bg-black/50 border-purple-500/30 text-white"
                    />
                  </div>

                  <div>
                    <Label className="text-purple-300">YouTube URL</Label>
                    <Input 
                      value={trackForm.youtube_url}
                      onChange={(e) => setTrackForm({ ...trackForm, youtube_url: e.target.value })}
                      placeholder="https://youtube.com/watch?v=..."
                      className="bg-black/50 border-purple-500/30 text-white"
                    />
                  </div>

                  {/* Publishing Options */}
                  <div className="border-t border-purple-500/20 pt-4 mt-2">
                    <Label className="text-purple-300 mb-3 block">Publishing</Label>
                    
                    <div className="flex gap-4 mb-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={trackForm.published === true}
                          onChange={() => setTrackForm({ ...trackForm, published: true, scheduled_publish_date: '' })}
                          className="text-purple-600"
                        />
                        <span className="text-white text-sm">Publish immediately</span>
                      </label>
                      
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={trackForm.published === false}
                          onChange={() => setTrackForm({ ...trackForm, published: false })}
                          className="text-purple-600"
                        />
                        <span className="text-white text-sm">Schedule for later</span>
                      </label>
                    </div>

                    {!trackForm.published && (
                      <div className="space-y-3 bg-purple-900/10 p-4 rounded-lg">
                        <div>
                          <Label className="text-purple-300 text-sm">Schedule Date & Time</Label>
                          <DateTimePicker
                            value={trackForm.scheduled_publish_date}
                            onChange={(v) => setTrackForm({ ...trackForm, scheduled_publish_date: v })}
                          />
                        </div>
                        
                        <div>
                          <Label className="text-purple-300 text-sm">Timezone</Label>
                          <select
                            value={trackForm.timezone}
                            onChange={(e) => setTrackForm({ ...trackForm, timezone: e.target.value })}
                            className="w-full bg-black/50 border border-purple-500/30 text-white rounded-lg p-2"
                          >
                            <option value="America/New_York">Eastern Time (ET)</option>
                            <option value="America/Chicago">Central Time (CT)</option>
                            <option value="America/Denver">Mountain Time (MT)</option>
                            <option value="America/Los_Angeles">Pacific Time (PT)</option>
                            <option value="Asia/Seoul">Korea Standard Time (KST)</option>
                            <option value="UTC">UTC</option>
                            <option value="Europe/London">London (GMT)</option>
                            <option value="Europe/Paris">Paris (CET)</option>
                            <option value="Asia/Tokyo">Tokyo (JST)</option>
                            <option value="Australia/Sydney">Sydney (AEDT)</option>
                          </select>
                        </div>
                        
                        <p className="text-purple-400 text-xs">
                          Track will be hidden until the scheduled date/time
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Featured Track Option for Scheduled Tracks */}
                  {!trackForm.published && (
                    <div className="border-t border-purple-500/20 pt-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={trackForm.is_featured || false}
                          onChange={(e) => setTrackForm({ ...trackForm, is_featured: e.target.checked })}
                          className="text-purple-600"
                        />
                        <span className="text-white text-sm">
                          ⭐ Set as featured track when published
                        </span>
                      </label>
                      <p className="text-purple-400 text-xs mt-1 ml-6">
                        This track will automatically become featured when its scheduled time arrives
                      </p>
                    </div>
                  )}

                  <div>
                    <Label className="text-purple-300">Audio File (MP3 only)</Label>
                    <input
                      type="file"
                      accept=".mp3,audio/mpeg"
                      onChange={handleAudioUpload}
                      className="hidden"
                      id="audio-upload"
                      disabled={uploading}
                    />
                    <label htmlFor="audio-upload">
                      <div className="border-2 border-dashed border-purple-500/30 rounded-lg p-8 text-center hover:border-purple-500/50 transition-colors cursor-pointer">
                        <Upload className="mx-auto h-12 w-12 text-purple-400 mb-2" />
                        <p className="text-purple-300 text-sm">
                          {uploading ? 'Uploading...' : trackForm.audio_url ? 'MP3 uploaded ✓' : 'Upload MP3 file'}
                        </p>
                        <p className="text-purple-400 text-xs mt-1">320kbps recommended for best quality</p>
                      </div>
                    </label>
                  </div>

                  <Button 
                    onClick={createTrack}
                    className="w-full bg-purple-600 hover:bg-purple-500"
                    disabled={uploading}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Track
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Bio & Settings</h2>
            
            <Card className="bg-gradient-to-br from-purple-900/20 to-black border-purple-500/30 p-6">
              <div className="space-y-6">
                <div>
                  <Label className="text-purple-300">Artist Bio</Label>
                  <Textarea 
                    value={settingsForm.bio}
                    onChange={(e) => setSettingsForm({ ...settingsForm, bio: e.target.value })}
                    className="bg-black/50 border-purple-500/30 text-white min-h-[200px]"
                  />
                </div>

                <div>
                  <Label className="text-purple-300">Contact Email</Label>
                  <Input 
                    type="email"
                    value={settingsForm.email}
                    onChange={(e) => setSettingsForm({ ...settingsForm, email: e.target.value })}
                    className="bg-black/50 border-purple-500/30 text-white"
                  />
                </div>

                <div>
                  <Label className="text-purple-300">YouTube Channel</Label>
                  <Input 
                    value={settingsForm.youtube}
                    onChange={(e) => setSettingsForm({ ...settingsForm, youtube: e.target.value })}
                    className="bg-black/50 border-purple-500/30 text-white"
                  />
                </div>

                <div>
                  <Label className="text-purple-300">Instagram</Label>
                  <Input 
                    value={settingsForm.instagram}
                    onChange={(e) => setSettingsForm({ ...settingsForm, instagram: e.target.value })}
                    className="bg-black/50 border-purple-500/30 text-white"
                  />
                </div>

                <div>
                  <Label className="text-purple-300">Profile Photo</Label>
                  {settingsForm.profile_image_url && (
                    <div className="mb-4">
                      <img 
                        src={settingsForm.profile_image_url}
                        alt="Profile"
                        className="w-32 h-32 rounded-lg object-cover border-2 border-purple-500/30"
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfileImageUpload}
                    className="hidden"
                    id="profile-upload"
                    disabled={uploading}
                  />
                  <label htmlFor="profile-upload">
                    <div className="border-2 border-dashed border-purple-500/30 rounded-lg p-8 text-center hover:border-purple-500/50 transition-colors cursor-pointer">
                      <ImageIcon className="mx-auto h-12 w-12 text-purple-400 mb-2" />
                      <p className="text-purple-300 text-sm">
                        {uploading ? 'Uploading...' : 'Click to upload new photo'}
                      </p>
                    </div>
                  </label>
                </div>

                <Button 
                  onClick={updateSettings}
                  className="bg-purple-600 hover:bg-purple-500"
                  disabled={uploading}
                >
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">📊 Analytics</h2>
            
            {/* Total Page Views */}
            <Card className="bg-gradient-to-br from-purple-900/20 to-black border-purple-500/30 p-6">
              <h3 className="text-xl font-bold text-white mb-4">Site Traffic</h3>
              <div className="flex items-center gap-4">
                <div className="bg-purple-600/20 rounded-lg p-4 flex-1">
                  <p className="text-purple-300 text-sm mb-1">Total Page Views</p>
                  <p className="text-4xl font-bold text-white">{analytics?.page_views?.toLocaleString() || 0}</p>
                </div>
              </div>
            </Card>

            {/* Track Plays by EP */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-white">Track Plays</h3>
              
              {analytics?.eps?.length === 0 && (
                <Card className="bg-gradient-to-br from-purple-900/20 to-black border-purple-500/30 p-6">
                  <p className="text-purple-300 text-center">No EPs or tracks yet. Create some to see analytics!</p>
                </Card>
              )}

              {analytics?.eps?.map(ep => (
                <Card key={ep.ep_id} className="bg-gradient-to-br from-purple-900/20 to-black border-purple-500/30 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-bold text-white">{ep.ep_title}</h4>
                    <span className="text-purple-400 font-medium">{ep.total_plays} total plays</span>
                  </div>
                  
                  <div className="space-y-2">
                    {ep.tracks.length === 0 ? (
                      <p className="text-purple-300 text-sm">No tracks in this EP yet</p>
                    ) : (
                      ep.tracks.map((track, index) => (
                        <div 
                          key={track.id} 
                          className="flex items-center justify-between bg-black/30 p-3 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-purple-400 text-sm w-6">{index + 1}.</span>
                            <span className="text-white">{track.title}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Play className="h-4 w-4 text-purple-400" />
                            <span className="text-purple-300 font-medium">{track.play_count?.toLocaleString() || 0}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};